import React, { useEffect, useRef } from 'react';
import { useWorldStore } from '../../state/worldStore';
import { useCityStore } from '../../city/cityStore';
import { useCutscene, cutsceneBridge } from '../../city/cutscene';
import { REPLAY_ID, useReplay, loadReplay, playReplay, replayLanding } from '../../city/replay';
import { introBridge } from '../../state/introBridge';
import { serverNow, cityHello } from '../../services/worldSocket';
import { audioEngine } from '../../services/audioEngine';

// The replay's own flat half (city/replay): it starts once you have arrived in
// the city, the radio steps aside while it plays, and at the end a card offers
// it again or hands the live city back. Only mounted to any effect with
// ?story=<id> in the address.

const ACCENT = '#ffb25e';
const card = {
  position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 90,
  width: 'min(420px, calc(100vw - 32px))', boxSizing: 'border-box', padding: '18px 20px 20px', borderRadius: 16,
  background: 'rgba(14,9,6,0.94)', border: '1px solid rgba(255,178,94,0.45)', color: '#ffe9c4',
  font: '14px/1.45 system-ui, sans-serif', boxShadow: '0 16px 50px rgba(0,0,0,0.5)', textAlign: 'center',
};
const btn = { border: 0, borderRadius: 999, padding: '10px 16px', background: ACCENT, color: '#1a1205', font: '800 13.5px system-ui', cursor: 'pointer' };
const ghost = { ...btn, background: 'transparent', color: '#ffe9c4', border: '1px solid rgba(255,178,94,0.45)' };
const chip = {
  position: 'fixed', left: '50%', top: 62, transform: 'translateX(-50%)', zIndex: 90, padding: '8px 14px', borderRadius: 999,
  background: 'rgba(16,10,6,0.86)', border: '1px solid rgba(255,178,94,0.45)', color: '#ffe9c4', font: '600 13px system-ui',
};

export default function ReplayDirector() {
  const state = useReplay((s) => s.state);
  const error = useReplay((s) => s.error);
  const episode = useReplay((s) => s.episode);
  const arrivedAt = useWorldStore((s) => s.arrivedAt);
  const target = useCutscene((s) => s.target);
  const interior = useWorldStore((s) => s.interior);
  const exitInterior = useWorldStore((s) => s.exitInterior);
  const stepped = useRef(false);

  // The episode plays on the street; inside a building none of it is there.
  // (Doors no longer open for a cutscene's camera — this is the backstop.)
  // EXCEPT a room the episode itself goes into (an indoor scene, 2026-10-29):
  // StoryRoomSwitch took the viewer in there on purpose.
  useEffect(() => {
    if (state !== 'playing' || !interior) return;
    if ((episode?.scenes || []).some((s) => s.room === interior)) return;
    exitInterior();
  }, [state, interior, exitInterior, episode]);

  useEffect(() => { if (REPLAY_ID) loadReplay(); }, []);

  // THE FLY-IN COMES DOWN ON THE FIRST SCENE (city/replay replayLanding): set
  // as the street's arrival before the descent starts — and only before, so a
  // landing is never moved mid-flight — re-worked as the city's places and the
  // road map come in (spots snap onto the pavement once it exists).
  useEffect(() => {
    if (!REPLAY_ID || state !== 'ready' || arrivedAt) return undefined;
    let last = null;
    const tryLand = () => {
      if (introBridge.active || useWorldStore.getState().arrivedAt) return;
      const pose = replayLanding(window.innerWidth / Math.max(1, window.innerHeight));
      const key = pose && [...pose.pos, ...pose.look].map((v) => v.toFixed(2)).join(',');
      if (pose && key !== last) { last = key; useWorldStore.getState().setStreetArrival(pose); }
    };
    tryLand();
    const iv = setInterval(tryLand, 1000);
    return () => clearInterval(iv);
  }, [state, arrivedAt]);

  // Arrived and loaded: play it, with the radio stepped aside — once frames are
  // actually being drawn again. Landing somewhere new can stall the first
  // moments (the street there loading in), and an episode runs on the clock:
  // started under the stall, its opening lines would pass unseen.
  useEffect(() => {
    if (!REPLAY_ID || state !== 'ready' || !arrivedAt) return undefined;
    audioEngine.roomQuiet(true, 900, 'replay').catch?.(() => {});
    stepped.current = false;
    let raf = 0;
    raf = requestAnimationFrame(() => { raf = requestAnimationFrame(() => { raf = 0; playReplay(serverNow()); }); });
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [state, arrivedAt]);

  // Leave pressed mid-episode: it is over for this viewing.
  useEffect(() => {
    if (state !== 'playing') return;
    if (target) { stepped.current = true; return; }
    if (stepped.current) useReplay.setState({ state: 'done' });
  }, [state, target]);

  useEffect(() => {
    if (state === 'done' || state === 'error') audioEngine.roomQuiet(false, 900, 'replay').catch?.(() => {});
    // The landing held the camera for the first scene (PlanetScene); an episode
    // that cannot play must not leave it held, or nobody can walk.
    if (state === 'error' && cutsceneBridge.active && !useCutscene.getState().target) cutsceneBridge.active = false;
  }, [state]);

  if (!REPLAY_ID || state === 'off') return null;
  if (state === 'loading') return <div style={chip}>Loading the episode…</div>;
  if (state === 'error') return <div style={{ ...chip, borderColor: 'rgba(255,120,120,0.6)' }}>Replay: {error}</div>;
  // Voices are recorded five minutes before an episode airs: before that, a
  // preview is the scenes and the captions, in silence — said, not left to guess.
  const unvoiced = state === 'playing' && !(episode?.lines || []).some((l) => l.audio);
  if (unvoiced) return <div style={{ ...chip, top: 'auto', bottom: 'calc(11vh + 12px)' }}>Not voiced yet: voices are recorded five minutes before it airs. Captions only.</div>;
  if (state !== 'done') return null;

  const backToCity = () => {
    useReplay.setState({ state: 'off' });
    useCityStore.setState({ replay: false });
    cityHello();
    try { const u = new URL(window.location.href); u.searchParams.delete('story'); u.searchParams.delete('rt'); window.history.replaceState(null, '', u.pathname + u.search + u.hash); } catch { /* no-op */ }
  };
  return (
    <div style={card} role="dialog" aria-label="Replay over">
      <div style={{ color: ACCENT, fontSize: 10.5, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase' }}>Replay</div>
      <div style={{ fontSize: 17, fontWeight: 800, margin: '4px 0 6px' }}>{episode?.name} · Episode {episode?.ep}: {episode?.title}</div>
      {episode?.logline && <div style={{ color: 'rgba(255,233,196,0.75)', fontSize: 13.5 }}>{episode.logline}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 14 }}>
        <button type="button" style={btn} onClick={() => useReplay.setState({ state: 'ready' })}>Watch again</button>
        <button type="button" style={ghost} onClick={backToCity}>Back to the live city</button>
      </div>
    </div>
  );
}
